
import { useEffect, useState } from 'react';
import AppLayout from '@/components/layout/AppLayout';
import AcquirerDataPanel from '@/components/adelantos/AcquirerDataPanel';
import EmptyState from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { httpClient } from '@/lib/http-client';
import { getEndpointUrl } from '@/lib/api-config';

interface AcquirerConnection {
  id: string;
  acquirerId: string;
  acquirerName: string;
  integrationStatus: string;
}

interface ListConnectionsResponse {
  connections: AcquirerConnection[];
}

export default function MovimientosPage() {
  const [connections, setConnections] = useState<AcquirerConnection[]>([]);
  const [selected, setSelected] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const url = getEndpointUrl('acquirerConnections', 'list');
        const data = await httpClient.get<ListConnectionsResponse>(url, { shouldCache: false });
        setConnections(data.connections);
        if (data.connections.length > 0) setSelected(data.connections[0].acquirerId);
      } catch {
        setConnections([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <AppLayout wide>
      <div className="py-6">
        <h1 className="text-2xl font-semibold text-foreground">Movimientos y cuotas</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Todas las ventas, abonos y cuotas detectados por tu adquirente. Desde acá puedes ver el estado de cobro de cada cuota y si está vinculada a un adelanto.
        </p>

        {loading ? (
          <div className="mt-6 space-y-3">
            <Skeleton className="h-10 w-full max-w-xs" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : connections.length === 0 ? (
          <div className="mt-6">
            <EmptyState
              icon="credit_card"
              title="Todavía no tienes adquirentes conectados"
              description="Conecta tu primer adquirente para empezar a ver las ventas, abonos y cuotas detectados automáticamente."
              action={{ label: 'Conectar adquirente', to: '/configuraciones/credenciales-adquirente' }}
            />
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            {connections.length > 1 && (
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium">Adquirente:</label>
                <select value={selected} onChange={(e) => setSelected(e.target.value)} className="rounded-md border border-input bg-background px-3 py-2 text-sm">
                  {connections.map((c) => (
                    <option key={c.acquirerId} value={c.acquirerId}>{c.acquirerName}</option>
                  ))}
                </select>
              </div>
            )}
            {selected && (
              <AcquirerDataPanel
                title="Ventas y cuotas"
                description="Datos sincronizados desde tu adquirente. Se actualizan automáticamente cada día."
                acquirerId={selected}
                buildUrl={(resource) => getEndpointUrl('acquirerData', resource).replace('{acquirerId}', selected)}
                fetcher={(url) => httpClient.get(url, { shouldCache: false })}
                advanceLinkPrefix="/adelantos/detalle?id="
              />
            )}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
